/**
 * Creazione manuale di un portafoglio (TODO.md): finora si creavano solo
 * dall'import. Tre campi (nome, tipo, piattaforma) più una valuta base
 * facoltativa, precompilata con quella dell'utente.
 */

import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/auth-context';
import { ApiError } from '@/lib/api-client';
import { PORTFOLIO_TYPE_LABELS } from '@/lib/format';
import { Alert, Button, Field, Modal, Select } from '@/ui';
import { useCreatePortfolio, usePlatforms } from './api';

const PORTFOLIO_TYPE_OPTIONS = Object.entries(PORTFOLIO_TYPE_LABELS).map(([value, label]) => ({ value, label }));

export function CreatePortfolioModal({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const platformsQuery = usePlatforms();
  const createPortfolio = useCreatePortfolio();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [portfolioType, setPortfolioType] = useState('securities');
  const [platformId, setPlatformId] = useState('');
  const [baseCurrency, setBaseCurrency] = useState(user?.baseCurrency ?? 'EUR');

  const error = createPortfolio.error instanceof ApiError ? createPortfolio.error : null;
  const fieldError = (path: string) =>
    error?.isValidation ? error.details.find((detail) => detail.path.includes(path))?.message : undefined;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!platformId) return;
    createPortfolio.mutate(
      {
        name: name.trim(),
        portfolioType,
        platformId,
        baseCurrency: baseCurrency.trim() ? baseCurrency.trim().toUpperCase() : undefined,
      },
      {
        onSuccess: (created) => {
          onClose();
          void navigate(`/portfolios/${created.id}`);
        },
      },
    );
  };

  const platformOptions = (platformsQuery.data?.items ?? []).map((platform) => ({
    value: platform.id,
    label: platform.name,
  }));

  return (
    <Modal title="Crea portafoglio" onClose={onClose}>
      <form className="transaction-form" onSubmit={handleSubmit}>
        <Field
          label="Nome"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Il mio portafoglio"
          required
          error={fieldError('name')}
        />
        <Select
          label="Tipo"
          value={portfolioType}
          onChange={(event) => setPortfolioType(event.target.value)}
          options={PORTFOLIO_TYPE_OPTIONS}
          required
          error={fieldError('portfolioType')}
        />
        <Select
          label="Piattaforma"
          value={platformId}
          onChange={(event) => setPlatformId(event.target.value)}
          options={platformOptions}
          placeholder={platformsQuery.isPending ? 'Caricamento…' : 'Scegli…'}
          required
          error={fieldError('platformId')}
        />
        <Field
          label="Valuta base (opzionale)"
          hint="Se vuota, usa la tua valuta base"
          value={baseCurrency}
          onChange={(event) => setBaseCurrency(event.target.value)}
          maxLength={3}
          placeholder="EUR"
          error={fieldError('baseCurrency')}
        />

        {portfolioType === 'paper' && (
          <Alert variant="info">
            Parte con una liquidità simulata di 10.000 {baseCurrency.trim().toUpperCase() || 'EUR'}: ogni
            acquisto/vendita registrato ne simula il movimento come su una vera piattaforma di trading.
          </Alert>
        )}

        <div className="transaction-form__actions">
          <Button type="submit" loading={createPortfolio.isPending} disabled={!platformId}>
            Crea
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            Annulla
          </Button>
        </div>

        {error && !error.isValidation && (
          <Alert variant="error" title="Creazione non riuscita">
            {error.message}
          </Alert>
        )}
      </form>
    </Modal>
  );
}
